import { BusinessSettingsLayout } from '@/components/business-settings/BusinessSettingsLayout';
import { HolidayCalendar } from '@/components/business-settings/HolidayCalendar';
import { useCompanySettings } from '@/hooks/useCompanySettings';
import { useUpdateWorkplaceDetails } from '@/hooks/business/useWorkplaceDetails';

export default function HolidaySettings() {
  const { settings: companySettings, updateSettings: updateCompanySettings } = useCompanySettings();
  // `country_code` is owned by Workplace General (update_workplace_details).
  // Holidays writes it through the same owner RPC so there is a single writer.
  const updateWorkplace = useUpdateWorkplaceDetails();

  const saveCountryCode = async (countryCode: string | null | undefined) => {
    if (!countryCode) return;
    if (countryCode === companySettings?.country_code) return;
    await updateWorkplace.mutateAsync({ countryCode });
  };

  return (
    <BusinessSettingsLayout
      title="Holidays"
      description="Manage company holidays and public holiday calendars"
    >
      <HolidayCalendar
        holidays={(companySettings?.holidays as string[]) || []}
        countryCode={companySettings?.country_code || null}
        regionCode={companySettings?.region_code || null}
        holidaySyncStatus={companySettings?.holiday_sync_status || null}
        holidayAutoSync={companySettings?.holiday_auto_sync ?? false}
        onAutoSyncChange={async (enabled) => {
          await updateCompanySettings({ holiday_auto_sync: enabled });
        }}
        onSave={async (holidays, additionalUpdates) => {
          const { country_code, ...rest } = (additionalUpdates ?? {}) as Record<string, unknown> & {
            country_code?: string | null;
          };
          // Country goes through its owner RPC first; if it fails we abort
          // before persisting holidays so the two never diverge.
          await saveCountryCode(country_code);
          await updateCompanySettings({
            holidays,
            ...rest,
          });
        }}
        onCountryChange={async (countryCode) => {
          await saveCountryCode(countryCode);
        }}
        onRegionChange={async (regionCode) => {
          await updateCompanySettings({ region_code: regionCode });
        }}
        isLoading={false}
      />
    </BusinessSettingsLayout>
  );
}
